/**
 * <AdminRoadmapPage> - Iter 9.40 Pass H
 *
 * Vertical timeline with phase/month/week/day granularity toggle, expand/collapse per item.
 * No financial numbers - just the calendar structure of category launches, feature rollouts,
 * and city activations.
 *
 * Backend: GET /api/admin/timeline?granularity=phase|month|week|day
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Loader2, Calendar, Flag, Sparkles, MapPin, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const KIND_META = {
  category_launch: { icon: Flag,     color: "bg-purple-50 text-purple-700" },
  city_activation: { icon: MapPin,   color: "bg-blue-50 text-blue-700" },
  feature_rollout: { icon: Sparkles, color: "bg-pink-50 text-pink-700" },
  city_setup:      { icon: Rocket,   color: "bg-emerald-50 text-emerald-700" },
};

export default function AdminRoadmapPage() {
  const [data, setData] = useState(null);
  const [granularity, setGranularity] = useState("phase");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/admin/timeline?granularity=${granularity}`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [granularity]);

  return (
    <div className="space-y-5" data-testid="admin-roadmap-page">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Calendar className="w-5 h-5" /> Roadmap
        </h2>
        <p className="text-sm text-gray-500">Category launches · feature rollouts · city activations, synced to Launch Control and Master Features.</p>
      </div>

      {/* Phase ribbon */}
      {(data?.phases || []).length > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="phase-ribbon">
          {data.phases.map((p) => (
            <div key={p.id} className="rounded-lg p-3 text-white text-xs" style={{ backgroundColor: p.color }}>
              <div className="font-bold">{p.label}</div>
              <div className="opacity-90 text-[10px]">{p.start} → {p.end}</div>
            </div>
          ))}
        </div>
      )}

      {/* Granularity toggle */}
      <div className="flex gap-2" data-testid="granularity-toggle">
        {["phase", "month", "week", "day"].map((g) => (
          <button
            key={g}
            onClick={() => setGranularity(g)}
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
              granularity === g ? "bg-black text-white" : "bg-gray-100 text-gray-700"
            }`}
            data-testid={`granularity-${g}`}
          >
            {g}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : !data ? (
        <div className="py-10 text-center text-sm text-gray-500">Failed to load timeline.</div>
      ) : (
        <div className="space-y-2" data-testid="timeline-groups">
          {(data.groups || []).map((g) => {
            const isOpen = expanded[g.key] !== false; // default open
            return (
              <Card key={g.key} data-testid={`timeline-group-${g.key}`}>
                <CardContent className="p-0">
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [g.key]: !isOpen }))}
                    className="w-full p-3 flex items-center gap-2 hover:bg-gray-50 text-left"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <span className="font-medium text-sm">{g.key}</span>
                    <Badge variant="secondary" className="text-[10px]">{g.count}</Badge>
                  </button>
                  {isOpen && (
                    <div className="border-t divide-y">
                      {g.items.map((item) => {
                        const meta = KIND_META[item.kind] || {};
                        const Icon = meta.icon || Flag;
                        return (
                          <div key={`${item.source}-${item.id}`} className="flex items-center gap-3 p-3" data-testid={`timeline-item-${item.source}-${item.id}`}>
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${meta.color || "bg-gray-50"}`}>
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{item.label}</div>
                              <div className="text-[10px] text-gray-500 flex gap-2 items-center">
                                <span className="capitalize">{item.kind.replace(/_/g, " ")}</span>
                                {item.date && <span>· {item.date.slice(0, 10)}</span>}
                                {item.state && <Badge variant="outline" className="text-[9px] py-0 capitalize">{item.state}</Badge>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {g.items.length === 0 && <p className="p-3 text-xs text-gray-400 italic">No items in this bucket.</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
